import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { Package, Plus, Trash, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';

const SECTOR_META = {
  construction: { label: 'Bouw', icon: '🏗️', color: '#F59E0B' },
  clothing: { label: 'Kleding', icon: '👕', color: '#EC4899' },
  food: { label: 'Voeding', icon: '🍎', color: '#10B981' },
  general: { label: 'Algemeen', icon: '📦', color: '#6B7280' },
};

export default function PackingConfigPanel() {
  const [configs, setConfigs] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [activeSector, setActiveSector] = useState('construction');
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    API.get('/packing/configs').then(r => {
      setConfigs(JSON.parse(JSON.stringify(r.data.sectors || {})));
      setDefaults(r.data.defaults || {});
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await API.put('/packing/configs', { sectors: configs });
      setDirty(false);
      setMsg('✅ Opgeslagen');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Opslaan mislukt'));
    }
  };

  const resetSector = () => {
    if (!defaults?.[activeSector]) return;
    if (!window.confirm(`Reset sector "${SECTOR_META[activeSector]?.label}" naar standaard regels?`)) return;
    setConfigs(p => ({ ...p, [activeSector]: JSON.parse(JSON.stringify(defaults[activeSector])) }));
    setDirty(true);
  };

  if (!configs) return <div className="p-4 text-gray-400 text-sm">Laden...</div>;

  const sec = configs[activeSector] || {};
  const isBundle = 'bundle_rules' in sec;
  const rulesKey = isBundle ? 'bundle_rules' : 'box_rules';
  const palletKey = isBundle ? 'bundles_per_pallet' : 'boxes_per_pallet';
  const rules = sec[rulesKey] || {};
  const palletRules = sec.pallet_rules || {};

  const updateRule = (oldKey, newKey, newVal) => {
    setConfigs(p => {
      const next = { ...p };
      const s = { ...next[activeSector] };
      const r = { ...s[rulesKey] };
      if (oldKey !== newKey) delete r[oldKey];
      if (newKey) r[newKey] = Number(newVal) || 0;
      s[rulesKey] = r;
      next[activeSector] = s;
      return next;
    });
    setDirty(true);
  };

  const deleteRule = (key) => {
    setConfigs(p => {
      const next = { ...p };
      const s = { ...next[activeSector] };
      const r = { ...s[rulesKey] };
      delete r[key];
      s[rulesKey] = r;
      next[activeSector] = s;
      return next;
    });
    setDirty(true);
  };

  const addRule = () => {
    setConfigs(p => {
      const next = { ...p };
      const s = { ...next[activeSector] };
      const r = { ...s[rulesKey] };
      r['nieuwe_regel_' + Date.now().toString().slice(-4)] = 10;
      s[rulesKey] = r;
      next[activeSector] = s;
      return next;
    });
    setDirty(true);
  };

  const updateScalar = (field, val, subField = null) => {
    setConfigs(p => {
      const next = { ...p };
      const s = { ...next[activeSector] };
      if (subField) {
        s[field] = { ...(s[field] || {}), [subField]: Number(val) || 0 };
      } else {
        s[field] = field === 'label' ? val : Number(val) || 0;
      }
      next[activeSector] = s;
      return next;
    });
    setDirty(true);
  };

  return (
    <div className="section-card" data-testid="packing-config-panel">
      <div className="section-header flex items-center justify-between">
        <span className="section-title inline-flex items-center gap-2">
          <Package size={18} weight="bold" /> Packing-configuratie per sector
        </span>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs font-bold">{msg}</span>}
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={resetSector} data-testid="packing-reset">
            <ArrowCounterClockwise size={13} /> Reset sector
          </button>
          <button className={`btn btn-sm inline-flex items-center gap-1 ${dirty ? 'btn-primary' : 'btn-secondary'}`} onClick={save} disabled={!dirty} data-testid="packing-save">
            <FloppyDisk size={13} /> Opslaan
          </button>
        </div>
      </div>

      <div className="section-body p-0">
        {/* Sector tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {Object.keys(SECTOR_META).map(k => {
            const m = SECTOR_META[k];
            const active = activeSector === k;
            return (
              <button
                key={k}
                onClick={() => setActiveSector(k)}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border-r border-gray-200 transition ${active ? 'bg-white text-gray-900' : 'text-gray-500 hover:bg-white/60'}`}
                style={active ? { borderBottom: `3px solid ${m.color}`, marginBottom: '-1px' } : {}}
                data-testid={`sector-tab-${k}`}
              >
                {m.icon} {m.label}
              </button>
            );
          })}
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Rules table */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                {isBundle ? 'Bundel-regels (stuks per bundel)' : 'Doos-regels (stuks per doos)'}
              </h4>
              <button className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1" onClick={addRule} data-testid="packing-add-rule">
                <Plus size={12} weight="bold" /> Regel toevoegen
              </button>
            </div>
            <div className="border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Product-match (contains, case-insensitive)</th>
                    <th className="text-right px-3 py-2 font-bold w-[140px]">Stuks / {isBundle ? 'bundel' : 'doos'}</th>
                    <th className="w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(rules).filter(k => k !== 'default').map((key) => (
                    <tr key={key} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">
                        <input
                          className="field-input font-mono text-xs"
                          defaultValue={key}
                          onBlur={e => updateRule(key, e.target.value.trim(), rules[key])}
                          data-testid={`rule-key-${key}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          min="1"
                          className="field-input text-right font-mono text-xs"
                          value={rules[key]}
                          onChange={e => updateRule(key, key, e.target.value)}
                          data-testid={`rule-val-${key}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button className="text-red-500 hover:text-red-700" onClick={() => deleteRule(key)} title="Verwijderen" data-testid={`rule-del-${key}`}>
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* Default rule */}
                  <tr className="border-t-2 border-gray-300 bg-blue-50/30">
                    <td className="px-3 py-2 text-xs font-semibold text-gray-700">
                      📌 Standaard (fallback als geen match)
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        min="1"
                        className="field-input text-right font-mono text-xs"
                        value={rules.default || 10}
                        onChange={e => updateRule('default', 'default', e.target.value)}
                        data-testid="rule-val-default"
                      />
                    </td>
                    <td></td>
                  </tr>
                  {Object.keys(rules).filter(k => k !== 'default').length === 0 && (
                    <tr><td colSpan="3" className="text-center text-gray-400 text-xs py-4 italic">Nog geen specifieke regels — enkel default actief. Voeg regels toe om producten met specifieke bundel-aantallen te configureren.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 italic">
              💡 Tip: product-match gebruikt <strong>contains</strong> (case-insensitive). Bv. "CD60/27" matcht "Kruisverbinder voor CD60/27".
            </p>
          </div>

          {/* Sidebar: pallets + overhead */}
          <div className="space-y-4">
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Sector-naam</h4>
              <input
                className="field-input text-sm"
                value={sec.label || ''}
                onChange={e => updateScalar('label', e.target.value)}
                data-testid="sector-label"
              />
            </div>
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Pallet-capaciteit</h4>
              <label className="text-[11px] text-gray-600 block mb-1">{isBundle ? 'Bundels' : 'Dozen'} per pallet</label>
              <input
                type="number"
                min="1"
                className="field-input text-right font-mono"
                value={palletRules[palletKey] || 30}
                onChange={e => updateScalar('pallet_rules', e.target.value, palletKey)}
                data-testid="pallet-capacity"
              />
            </div>
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Verpakkings-overhead</h4>
              <label className="text-[11px] text-gray-600 block mb-1">% extra op netto voor bruto</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  className="field-input text-right font-mono flex-1"
                  value={sec.packaging_weight_percent ?? 5}
                  onChange={e => updateScalar('packaging_weight_percent', e.target.value)}
                  data-testid="packaging-overhead"
                />
                <span className="text-sm font-mono">%</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1 italic">Bruto = Netto × (1 + {sec.packaging_weight_percent || 0}%/100)</p>
            </div>
            <div className="border border-gray-200 p-3 bg-gray-50">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Gewicht-modus</h4>
              <label className="flex items-center gap-2 text-[11px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!sec.use_length_weight}
                  onChange={e => updateScalar('use_length_weight', e.target.checked ? 1 : 0)}
                  data-testid="use-length-weight"
                />
                Gebruik lengte × gewicht_per_meter (bouw)
              </label>
              <p className="text-[10px] text-gray-500 mt-1 italic">
                {sec.use_length_weight ? '📐 Netto = lengte × aantal × gewicht/meter' : '📦 Netto = gewicht_per_stuk × aantal'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
